import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../../components/Utils/CallApi";
import { setPageLoadingTrue, setPageLoadingFalse } from "../appSlice";
import { setError } from "../appSlice";

export const fetchSectionTypes = createAsyncThunk(
  "promotions/fetchSectionTypes",
  async ({ companyId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `companyId=${companyId}`;
      const response = await get("sections/type/list", params);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const fetchPromotions = createAsyncThunk(
  "promotions/fetchPromotions",
  async ({ companyId, page, limit }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `companyId=${companyId}&sectionType=promotions&page=${page}&limit=${limit}`;
      const response = await get("sections/data/list", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const savePromotion = createAsyncThunk(
  "promotions/savePromotion",
  async (
    { payload, sectionId, files },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();
      const response = await post(
        "sections/data/save",
        payload,
        state.app,
        sectionId,
        files
      );

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);
export const deleteSectionData = createAsyncThunk(
  "promotions/deleteSectionData",
  async ({ id }, { dispatch, getState, rejectWithValue }) => {
    debugger;
    try {
      dispatch(setPageLoadingTrue());
      const state = getState();

      const response = await post("sections/data/delete", { id }, state.app);

      if (response.status === "failure") {
        dispatch(setError("An error has occurred"));
        return rejectWithValue(response.result);
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const orderSectionData = createAsyncThunk(
  "promotions/orderSectionData",
  async (reorderedData, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());

      // Add the Authorization token to the body of the request
      const response = await post("sections/data/sort", {
        reorderedData: reorderedData,
      });

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }
      return response.data;
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

export const getPromotion = createAsyncThunk(
  "promotions/getPromotion",
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPageLoadingTrue());
      const params = `id=${id}&sectionType=promotions`;
      const response = await get("sections/data/get", params);

      if (response.status === "failure") {
        dispatch(setError(response.result));
        return rejectWithValue(response.result);
      }

      return response.result || [];
    } catch (error) {
      dispatch(setError(error.message));
      return rejectWithValue(error.message);
    } finally {
      dispatch(setPageLoadingFalse());
    }
  }
);

const promotionsSlice = createSlice({
  name: "promotions",
  initialState: {
    promotionsContent: [],
    sectionTypes: [], // Add sectionTypes to the state
    loading: false,
    error: null,
  },
  reducers: {
    addPromotion: (state, action) => {
      if (action.payload) {
        state.promotionsContent.push(action.payload);
      }
    },
    editPromotion: (state, action) => {
      const index = state.promotionsContent.findIndex(
        (promotion) => promotion.id === action.payload.id
      );
      if (index !== -1) {
        state.promotionsContent[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetching promotions
      .addCase(fetchPromotions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromotions.fulfilled, (state, action) => {
        state.loading = false;
        state.promotionsContent = action.payload;
      })
      .addCase(fetchPromotions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle saving promotions
      .addCase(savePromotion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(savePromotion.fulfilled, (state, action) => {
        state.loading = false;
        state.promotionsContent = action.payload; // Ensure full replacement
      })
      .addCase(savePromotion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetching section types
      .addCase(fetchSectionTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSectionTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.sectionTypes = action.payload;
      })
      .addCase(fetchSectionTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle deleting section data
      .addCase(deleteSectionData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSectionData.fulfilled, (state, action) => {
        state.loading = false;
        state.promotionsContent = state.promotionsContent.filter(
          (promotion) => promotion.id !== action.payload
        );
      })
      .addCase(deleteSectionData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle ordering section data
      .addCase(orderSectionData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(orderSectionData.fulfilled, (state, action) => {
        state.loading = false;

        const response = action.payload;

        // Ensure response is properly handled
        if (response?.status === "success") {
          const reorderedData = action.meta.arg; // The reordered list passed to the API
          state.promotionsContent = reorderedData.map((item) => {
            const existingItem = state.promotionsContent.find(
              (promo) => promo.id === item.id
            );
            return { ...existingItem, sortIndex: item.sortIndex };
          });
        } else {
          console.error("Unexpected API response:", response);
        }
      })
      .addCase(orderSectionData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { addPromotion, editPromotion } = promotionsSlice.actions;
export default promotionsSlice.reducer;
